import config from "config.js";
import store from "store";
import { addJRR } from "core-module/services/api/actions";
import parseApiStatus from "core-module/services/api/parseApiStatus";
import {
  getRememberedFirebaseToken,
  signInWithApple,
  signInWithGoogle,
  signInWithMicrosoft,
} from "./firebaseAuth";
import { isNativeIosPlatform } from "./deviceInfo";

const FIREBASE_AUTH_ENDPOINT = "guest/firebaseauth";
const FIREBASE_EXCHANGE_TIMEOUT_MS = 15000;

export const exchangeFirebaseTokenForJrr = async (
  firebaseToken,
  { signal } = {}
) => {
  if (signal?.aborted) {
    return false;
  }

  const controller = new AbortController();
  const cancelExchange = () => controller.abort();
  if (signal) {
    signal.addEventListener("abort", cancelExchange);
  }
  const timeoutId = isNativeIosPlatform()
    ? window.setTimeout(() => controller.abort(), FIREBASE_EXCHANGE_TIMEOUT_MS)
    : undefined;

  try {
    const tokenResponse = await fetch(
      `${config.API.ADDRESS}?target=${FIREBASE_AUTH_ENDPOINT}`,
      {
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: firebaseToken }),
        signal: controller.signal,
      }
    );
    const tokenData = await parseApiStatus(tokenResponse);

    // Cancellation can happen while the response body is being parsed, even
    // if fetch has already resolved. Never commit an obsolete session.
    if (signal?.aborted || controller.signal.aborted) {
      return false;
    }
    if (tokenData && "JRR" in tokenData) {
      store.dispatch(addJRR(tokenData.JRR));
      return true;
    }
    return false;
  } catch (e) {
    if (signal?.aborted) {
      return false;
    }
    if (e.name === "AbortError") {
      throw new Error(
        "Sign-in timed out while connecting to MyAccount. Please try again."
      );
    }
    throw e;
  } finally {
    window.clearTimeout(timeoutId);
    if (signal) {
      signal.removeEventListener("abort", cancelExchange);
    }
  }
};

export const restoreApiAccessFromFirebase = async ({ signal } = {}) => {
  const firebaseToken = await getRememberedFirebaseToken();
  if (!firebaseToken || signal?.aborted) {
    return false;
  }

  return exchangeFirebaseTokenForJrr(firebaseToken, { signal });
};

export const signInWithGoogleForApiAccess = async () => {
  const firebaseToken = await signInWithGoogle();
  return exchangeFirebaseTokenForJrr(firebaseToken);
};

export const signInWithAppleForApiAccess = async () => {
  const firebaseToken = await signInWithApple();
  if (!firebaseToken) {
    return false;
  }

  return exchangeFirebaseTokenForJrr(firebaseToken);
};

export const signInWithMicrosoftForApiAccess = async () => {
  const firebaseToken = await signInWithMicrosoft();
  return exchangeFirebaseTokenForJrr(firebaseToken);
};
